import { useState } from "react";
import { motion } from "framer-motion";
import { Ticket, CalendarDays, MapPin, Check, Minus, Plus, ShieldCheck, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import NationalNav from "@/components/national/NationalNav";
import NationalFooter from "@/components/national/NationalFooter";
import { VARNA_2027, TICKET_2027, TOPICS, IMG } from "@/lib/platform";
import { trackInitiateCheckout, prepareStripeCheckout } from "@/lib/metaPixel";

export default function Varna2027() {
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const total = qty * TICKET_2027.price;

  const checkout = () => {
    const checkoutUrl = prepareStripeCheckout({
      url: `${TICKET_2027.url}?quantity=${qty}`,
      ticketType: "early_bird_2027",
      value: total,
      currency: "EUR",
      contentName: TICKET_2027.contentName,
    });
    trackInitiateCheckout({
      value: total,
      currency: "EUR",
      content_name: TICKET_2027.contentName,
      content_ids: [TICKET_2027.contentId],
      num_items: qty,
    });
    window.open(checkoutUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <main data-testid="varna-2027-page" className="relative bg-[#0B1F3D] text-[#F5EBD9] min-h-screen">
      <NationalNav />

      {/* Hero */}
      <section className="relative pt-32 sm:pt-40 pb-16 lg:pb-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMG.varna})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3D]/60 to-[#0B1F3D]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <button
            onClick={() => navigate("/")}
            data-testid="v27-back"
            className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-[#8A9EB8] hover:text-[#F5892E] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.2} />
            Начало
          </button>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5892E] mb-4 flex items-center gap-4">
              <span className="h-px w-10 bg-[#F5892E]" />
              Следващо събитие
            </div>
            <h1 className="font-display uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.92] tracking-tight">
              Варна 2027.<br />
              <span className="text-[#F5892E]">Играта продължава.</span>
            </h1>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
              <div className="flex items-center gap-3 text-lg text-[#C8D4E6]">
                <CalendarDays className="w-5 h-5 text-[#F5892E]" strokeWidth={2.2} />
                {VARNA_2027.date}
              </div>
              <div className="flex items-center gap-3 text-lg text-[#C8D4E6]">
                <MapPin className="w-5 h-5 text-[#F5892E]" strokeWidth={2.2} />
                Варна, България · {VARNA_2027.venue}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ticket purchase */}
      <section className="relative pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Ticket card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            data-testid="v27-ticket-card"
            className="bg-[#0F284B] border-2 border-[#F5892E] rounded-2xl p-8 sm:p-10 shadow-[0_0_80px_-20px_rgba(245,137,46,0.45)] lg:sticky lg:top-28"
          >
            <div className="inline-flex items-center gap-2 bg-[#F5892E] text-[#0B1F3D] font-mono text-[10px] uppercase tracking-[0.26em] font-bold px-3.5 py-1.5 rounded-full mb-7">
              <Ticket className="w-3.5 h-3.5" strokeWidth={2.6} />
              Early Bird
            </div>

            <div className="flex items-baseline gap-3">
              <span className="font-display text-8xl leading-none tracking-tight">{TICKET_2027.price}</span>
              <span className="font-display text-3xl text-[#F5892E]">€</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#8A9EB8] ml-2">/ билет</span>
            </div>

            {/* Quantity */}
            <div className="mt-8 flex items-center justify-between border border-[#1E3A63] rounded-xl px-5 py-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#C8D4E6]">Брой билети</span>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  data-testid="v27-qty-minus"
                  aria-label="По-малко"
                  className="w-9 h-9 rounded-full border border-[#F5EBD9]/25 flex items-center justify-center hover:border-[#F5892E] hover:text-[#F5892E] transition-colors"
                >
                  <Minus className="w-4 h-4" strokeWidth={2.4} />
                </button>
                <span className="font-display text-2xl w-8 text-center" data-testid="v27-qty">{qty}</span>
                <button
                  onClick={() => setQty(Math.min(10, qty + 1))}
                  data-testid="v27-qty-plus"
                  aria-label="Повече"
                  className="w-9 h-9 rounded-full border border-[#F5EBD9]/25 flex items-center justify-center hover:border-[#F5892E] hover:text-[#F5892E] transition-colors"
                >
                  <Plus className="w-4 h-4" strokeWidth={2.4} />
                </button>
              </div>
            </div>

            {/* Total */}
            <div className="mt-5 flex items-center justify-between px-1">
              <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#C8D4E6]">Общо</span>
              <span className="font-display text-4xl text-[#F5892E]" data-testid="v27-total">{total} €</span>
            </div>

            <button
              onClick={checkout}
              data-testid="v27-checkout"
              className="mt-8 w-full py-5 rounded-full bg-[#F5892E] text-[#0B1F3D] font-bold text-base uppercase tracking-widest hover:bg-[#F5EBD9] transition-colors inline-flex items-center justify-center gap-3"
            >
              <Ticket className="w-5 h-5" strokeWidth={2.4} />
              Вземи билет — {total} €
            </button>

            <div className="mt-5 flex items-center justify-center gap-2 text-[#8A9EB8]">
              <ShieldCheck className="w-4 h-4" strokeWidth={2} />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Сигурно плащане чрез Stripe</span>
            </div>

            <div className="mt-6 pt-6 border-t border-[#1E3A63] flex flex-wrap justify-center gap-x-6 gap-y-2">
              {["Условия за билети", "Политика за възстановяване", "Поверителност"].map((l) => (
                <button
                  key={l}
                  data-testid={`v27-legal-${l}`}
                  className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A9EB8] underline underline-offset-4 hover:text-[#F5892E] transition-colors"
                >
                  {l}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="space-y-10"
          >
            <div>
              <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight mb-5">
                Какво включва билетът
              </h2>
              <ul className="space-y-4">
                {[
                  "Цял ден с топ лектори, панели и реални стратегии",
                  "Достъп до общността на инвеститорите",
                  "Кафе паузи и нетуъркинг включени",
                  "Материали и контакти след събитието",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[#C8D4E6]">
                    <Check className="w-5 h-5 text-[#F5892E] shrink-0 mt-0.5" strokeWidth={2.4} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight mb-5">
                Темите на събитието
              </h2>
              <div className="space-y-3">
                {TOPICS.slice(0, 4).map((t) => (
                  <div key={t.title} className="border border-[#1E3A63] rounded-xl px-5 py-4 bg-[#0F284B]/60">
                    <div className="font-semibold text-[#F5EBD9]">{t.title}</div>
                    <div className="text-sm text-[#8A9EB8] mt-1">{t.text}</div>
                  </div>
                ))}
              </div>
              <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.24em] text-[#8A9EB8]">
                Официалната програма и лекторите за 2027 ще бъдат обявени по-късно.
              </p>
            </div>

            <div className="border border-[#1E3A63] rounded-xl p-6 bg-[#0F284B]/60">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#F5892E] mb-2">
                Въпроси за билети
              </div>
              <p className="text-sm text-[#C8D4E6] leading-relaxed">
                Пиши ни на <span className="text-[#F5EBD9]">ivo@dimovski.bg</span> — отговаряме лично
                и бързо.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <NationalFooter />
    </main>
  );
}
